import "./MetricsTable.scss";
import {
  AdInfoRow,
  CellData,
  ColumnMetaData,
  ColumnMetaDataMap,
  getExportedData,
  getHeatMap,
  getHeatMapInfo as getHeatMapInfoUtil,
  getTextWidth,
  sortData,
  SuperHeader,
  TEXT_FONT_SIZE_MAP,
  TEXT_SIZE_MAP,
} from "./metricsTableUtils";
import {
  CellRenderer,
  CornerLocation,
  Dropdown,
  DropdownToggleType,
  Img,
  OverlayTrigger,
  Placement,
  StickyTable,
  SuperTopItemType,
  Virtual,
} from "../../Components";
import { Column, DimensionColumn } from "@blisspointmedia/bpm-types/dist/MetricsTable";
import { FUNCTIONS, OPERATORS } from "../MetricsTableConfig/CustomMetricsModal";
import { makeMemoizedGetter } from "../../utils/data";
import { MdArrowDownward, MdArrowUpward } from "react-icons/md";
import { SortInfo } from "@blisspointmedia/bpm-types/dist/Performance";
import { StateSetter } from "../../utils/types";
import { Tooltip } from "react-bootstrap";
import * as R from "ramda";
import * as uuid from "uuid";
import cn from "classnames";
import PercentAOIPlaceholder from "../../Performance/PercentAOIPlaceholder";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import CreativeTray from "../CreativeTray/CreativeTray";
import { useExperimentFlag } from "../../utils/experiments/experiment-utils";
import {
  calculateDisplayedMetricsMap,
  calculateHeatMapRanges,
  DisplayedMetrics,
  generateHeatMaps,
  IntermediateMetrics,
} from "../CreativeTray/creativeTrayUtils";
import { HeatMap } from "../../utils/colors";

interface RenderProps {
  classes?: string[];
  columnIndex: number;
  data:
    | CellData // Table Cells
    | Column // Table Headers
    | Record<string, CellData> // Dimension Cells
    | SuperHeader; // Super Headers
  rowIndex?: number;
  style?: object;
  virtual?: Virtual;
}

export interface OverrideParams {
  colPadding?: number;
  colWidth?: number | number[];
  dimensionColWidth?: number | number[];
  maxColWidth?: number;
  rowHeight?: number | number[];
  rowPadding?: number;
  headerTextHeight?: number;
}

interface MetricsTableProps {
  aggregateData: CellData[];
  columnMetaDataMap?: ColumnMetaDataMap;
  dataColumns: Column[];
  defaultSorting?: SortInfo[];
  dimensionColumns: DimensionColumn[];
  dimensionData: Record<string, CellData>[];
  disableMultiSorting?: boolean;
  exportedData?: (string | number)[][];
  height?: number;
  overrideParams?: OverrideParams;
  percentAOIPlaceholder?: number;
  role?: number; // We don't want to use to redux here because we cannot use it in storybook testing
  setExportedData?: StateSetter<(string | number)[][]>;
  superHeaders?: SuperTopItemType<SuperHeader>[];
  tableData: CellData[][];
  variant?: "Metrics" | "Widget" | "SparkChart";
  width?: number;
  kpiName?: string;
}

const DEFAULT_HEIGHT = 500;
const DEFAULT_WIDTH = 1204;

export const MetricsTable = React.memo<MetricsTableProps>(
  ({
    aggregateData = [],
    columnMetaDataMap = {},
    dataColumns = [],
    defaultSorting = [],
    dimensionColumns = [],
    dimensionData = [],
    disableMultiSorting = true,
    exportedData,
    height = DEFAULT_HEIGHT,
    overrideParams = {},
    percentAOIPlaceholder,
    role = 10,
    setExportedData = ((exportedData: (string | number)[][]) => {}) as StateSetter<
      (string | number)[][]
    >,
    superHeaders: superHeadersInput,
    tableData = [],
    variant = "Metrics",
    width = DEFAULT_WIDTH,
    kpiName,
  }) => {
    const {
      colPadding: overrideColPadding,
      colWidth: overrideColWidth,
      dimensionColWidth: overrideDimensionColWidth,
      maxColWidth: overrideMaxColWidth,
      rowHeight: overrideRowHeight,
      rowPadding: overrideRowPadding,
      headerTextHeight: overrideHeaderTextHeight,
    } = overrideParams as OverrideParams;
    const COL_DIVIDER_BORDER_WIDTH = 2;
    const DIMENSION_COLUMNS_RIGHT_MARGIN =
      !R.isNil(overrideColPadding) || !R.isNil(overrideColWidth) ? 0 : 16;
    const FOOTER_CONTAINER_BOTTOM_PADDING = 16;
    const FOOTER_CONTAINER_TOP_PADDING = 8;
    const FOOTER_TABLE_PADDING = 32;
    const FOOTER_HEIGHT = 63 + FOOTER_TABLE_PADDING;
    const FOOTER_PADDING = variant === "SparkChart" ? 0 : 4;
    const HEADER_FOOTER_PADDING = variant === "SparkChart" ? 0 : 3;
    const HEADER_SUBHEADER_GAP = 4;
    const HEADER_TEXT_HEIGHT = !R.isNil(overrideHeaderTextHeight) ? overrideHeaderTextHeight : 23;
    const LEFT_BORDER_WIDTH = 4;
    const LOGO_WIDTH = 50;
    const MAX_COL_PADDING = variant === "Metrics" ? 72 : 24;
    const MAX_DIM_WIDTH = !R.isNil(overrideMaxColWidth)
      ? 2 * overrideMaxColWidth
      : variant === "Metrics"
      ? 224
      : variant === "SparkChart"
      ? 112
      : 56;
    const MAX_GRID_COL_WIDTH = !R.isNil(overrideMaxColWidth)
      ? overrideMaxColWidth
      : variant === "Widget"
      ? 56
      : 136;
    const MIN_COL_PADDING = variant === "Metrics" ? 12 : variant === "SparkChart" ? 8 : 4;
    const MIN_HEADER_CONTAINER_HEIGHT = 43;
    const MIN_ROW_HEIGHT = variant === "Widget" ? 24 : 28;
    const ROW_PADDING = !R.isNil(overrideRowPadding)
      ? overrideRowPadding
      : variant === "Metrics"
      ? 5
      : variant === "SparkChart"
      ? 4
      : 0;
    const SUB_HEADER_HEIGHT = 20;
    const SUPER_HEADER_PADDING = 16;
    const TEXT_WIDTH_MARGIN = 4;
    const THREE_LINE_HEADER_TEXT_HEIGHT = 53;
    const THREE_LINE_ROW_HEIGHT = 58;
    const THUMBNAIL_WIDTH = 90;
    const TWO_LINE_HEADER_TEXT_HEIGHT = 38;
    const TWO_LINE_ROW_HEIGHT = 43;

    const dataHeaders: Column[] = useMemo(
      () =>
        R.map(
          column => ({
            ...column,
            id: R.defaultTo(uuid.v4(), column.id),
          }),
          R.filter(
            col => (col && col.role ? col.role >= role : true),
            dataColumns ? dataColumns : []
          ) as Column[]
        ),
      [dataColumns, role]
    );

    const dimensionDataHeaders: DimensionColumn[] = useMemo(
      () =>
        R.map(
          column => ({
            ...column,
            id: R.defaultTo(uuid.v4(), column.id),
          }),
          R.filter(
            col => (col && col.role ? col.role >= role : true),
            dimensionColumns ? dimensionColumns : []
          ) as DimensionColumn[]
        ),
      [dimensionColumns, role]
    );

    const hasAggregateData = useMemo(() => !R.isEmpty(aggregateData), [aggregateData]);
    const hasDimensionData = useMemo(
      () => !R.isEmpty(dimensionData) && !R.isEmpty(dimensionDataHeaders),
      [dimensionData, dimensionDataHeaders]
    );
    const TABLE_RIGHT_PADDING = hasDimensionData ? 16 : 0;
    const hasDataHeaders = useMemo(() => !R.isEmpty(dataHeaders), [dataHeaders]);
    const hasSuperHeaders = useMemo(
      () => !R.isNil(superHeadersInput) && !R.isEmpty(superHeadersInput),
      [superHeadersInput]
    );
    const SUPER_HEADER_HEIGHT = hasSuperHeaders ? 26 + 2 * 16 : 0;

    const [sorting, setSorting] = useState<SortInfo[]>(defaultSorting ? defaultSorting : []);

    // CREATIVE TRAY STATE
    const [creativeTrayOpen, setCreativeTrayOpen] = useState(false);
    const [creativeTrayMetrics, setCreativeTrayMetrics] = useState<DisplayedMetrics>({
      spend: -1,
      cpc: -1,
      ctr: -1,
      ccr: -1,
      volume: -1,
      revenue: -1,
      roas: -1,
      cpx: -1,
    });
    const [adInfo, setAdInfo] = useState<AdInfoRow>({
      company: "",
      platform: "",
      ad_name: "",
      ad_id: "",
      account_id: "",
    });
    const [heatMaps, setHeatMaps] = useState<Record<keyof DisplayedMetrics, HeatMap>>(
      {} as Record<keyof DisplayedMetrics, HeatMap>
    );
    // END CREATIVE TRAY STATE

    const [selectedThumbnailSize, setSelectedThumbnailSize] = useState<
      "small" | "medium" | "large"
    >("small");

    const shouldEnableMotionReplacement = useExperimentFlag("enableMotionReplacement");

    const addSorting = useCallback(
      (id: string, disableMulti = false) => {
        //  If a user selects  an already-sorted column, it flips it if it's descending, and removes it otherwise.
        setSorting(sorting => {
          let newSorting: SortInfo[] = [];
          let sawSelf = false;
          for (const sortItem of sorting) {
            if (sortItem.id === id) {
              sawSelf = true;
              // If it's descending, then flip. Otherwise, skip.
              if (!sortItem.asc) {
                newSorting.push({ id, asc: true });
              }
            } else {
              newSorting.push(sortItem);
            }
          }
          if (!sawSelf) {
            if (disableMulti) {
              // If we pick an entirely new column to sort, we want to clear out
              // all the current sorting.
              newSorting = [{ id, asc: false }];
            } else {
              newSorting.push({ id, asc: false });
            }
          }
          return newSorting;
        });
        setExportedData([]);
      },
      [setExportedData]
    );

    const sort = useCallback(
      (
        dataHeaders: Column[],
        dimensionData: Record<string, CellData>[],
        dimensionDataHeaders: DimensionColumn[],
        sorting: SortInfo[],
        tableData: CellData[][]
      ) => {
        return sortData(
          dataHeaders,
          dimensionData,
          dimensionDataHeaders,
          sorting,
          tableData,
          hasDimensionData
        );
      },
      [hasDimensionData]
    );

    const [sortedDimensionData, sortedTableData] = useMemo(
      () => sort(dataHeaders, dimensionData, dimensionDataHeaders, sorting, tableData),
      [sorting, sort, dataHeaders, dimensionData, dimensionDataHeaders, tableData]
    );
    const numDimCols = dimensionDataHeaders.length;
    const numDataCols = dataHeaders.length;
    const numCols = numDataCols + numDimCols;
    const numRows = tableData.length;

    useEffect(() => {
      if (sortedDimensionData && sortedTableData) {
        const newExportedData = getExportedData({
          columnMetaDataMap,
          dataHeaders,
          dimensionDataHeaders,
          numDataCols,
          numDimCols,
          sortedDimensionData,
          sortedTableData,
        });
        if (
          R.isNil(exportedData) ||
          R.isEmpty(exportedData) ||
          !R.equals(exportedData, newExportedData)
        ) {
          setExportedData(newExportedData);
        }
      }
    }, [
      columnMetaDataMap,
      dataHeaders,
      dimensionDataHeaders,
      exportedData,
      numDataCols,
      numDimCols,
      setExportedData,
      sortedDimensionData,
      sortedTableData,
    ]);

    // CREATIVE TRAY DATA PROCESSING
    const displayedMetricsMap = useMemo(() => {
      if (!shouldEnableMotionReplacement) {
        return {};
      }

      const metricsMap: Record<string, DisplayedMetrics> = {};

      sortedTableData.forEach((row, rowIndex) => {
        const adNameCell = sortedDimensionData[rowIndex]?.["Ad Name"];
        const adName = adNameCell?.label;

        if (!adName) {
          return;
        }

        const intermediateMetrics: IntermediateMetrics = {
          spend: -1,
          impressions: -1,
          clicks: -1,
          volume: -1,
          revenue: -1,
          roas: -1,
          cpx: -1,
        };

        dataHeaders.forEach((header, colIndex) => {
          const cellData = row[colIndex];
          if (cellData && typeof cellData.value === "number") {
            intermediateMetrics[header.dataVarName] = cellData.value;
          }
        });

        const displayedMetrics = calculateDisplayedMetricsMap(intermediateMetrics);

        metricsMap[adName] = displayedMetrics;
      });
      return metricsMap;
    }, [shouldEnableMotionReplacement, dataHeaders, sortedDimensionData, sortedTableData]);

    const heatMapRanges = useMemo(() => {
      if (!shouldEnableMotionReplacement) {
        return null;
      }
      return calculateHeatMapRanges(displayedMetricsMap);
    }, [shouldEnableMotionReplacement, displayedMetricsMap]);

    useEffect(() => {
      if (!shouldEnableMotionReplacement || !heatMapRanges) {
        return;
      }
      const newHeatMaps = generateHeatMaps(heatMapRanges);
      setHeatMaps(newHeatMaps);
    }, [shouldEnableMotionReplacement, heatMapRanges]);
    // END CREATIVE TRAY DATA PROCESSING

    const heatMap = useMemo(() => getHeatMap(dataHeaders, sortedTableData), [
      dataHeaders,
      sortedTableData,
    ]);

    const getHeatMapInfo = useMemo(() => {
      if (sortedTableData) {
        return makeMemoizedGetter({
          calculate: ({ columnIndex, value }: { columnIndex: number; value: number }) => {
            return getHeatMapInfoUtil(columnIndex, columnMetaDataMap, dataHeaders, heatMap, value);
          },
        });
      } else {
        return () => ({ backgroundColor: "transparent", outline: "none" });
      }
    }, [columnMetaDataMap, dataHeaders, heatMap, sortedTableData]);

    const innerCellRenderer = (
      innerCellContent,
      overlayText,
      overlayPlacement: Placement = "right center"
    ) => {
      return overlayText ? (
        <OverlayTrigger
          placement={overlayPlacement}
          overlay={<Tooltip id={overlayText}>{overlayText}</Tooltip>}
        >
          {innerCellContent}
        </OverlayTrigger>
      ) : (
        innerCellContent
      );
    };

    const superHeaders: SuperTopItemType<SuperHeader>[] = useMemo(
      () =>
        superHeadersInput && !R.isEmpty(superHeadersInput)
          ? superHeadersInput
          : [
              {
                data: { text: "", leftDivider: false, rightDivider: false },
                span: numDataCols,
              },
            ],
      [numDataCols, superHeadersInput]
    );

    const { leftColWidths, colWidths, heatMappingWidths, rowHeights } = useMemo(() => {
      const leftColWidths = {};
      const colWidths = {};
      const heatMappingWidths = {};
      const rowHeights: Record<string, number> = {};

      const THUMBNAIL_SIZES = {
        small: { width: 42, height: 50 },
        medium: { width: 100, height: 100 },
        large: { width: 180, height: 180 },
      };

      if (sortedDimensionData && sortedTableData) {
        for (let rowIndex = 0; rowIndex < numRows; rowIndex++) {
          const numCols = R.max(numDataCols, numDimCols);
          for (let colIndex = 0; colIndex < numCols; colIndex++) {
            const dimColIndex = R.min(colIndex, numDimCols - 1);
            const dataColIndex = R.min(colIndex, numDataCols - 1);
            // Calculate Header/Footer Widths
            if (rowIndex === 0) {
              // Dimension Header Widths
              const leftHeader = R.defaultTo([], dimensionDataHeaders)[dimColIndex];
              const genericDimColumnMetaData = columnMetaDataMap[leftHeader.dimensionTypeName];
              const dimensionHeaderLabel = R.defaultTo(
                R.defaultTo({}, genericDimColumnMetaData).displayName,
                leftHeader.label
              );
              if (dimensionHeaderLabel) {
                const labelWidth =
                  Math.ceil(getTextWidth(dimensionHeaderLabel, 14, 501)) + TEXT_WIDTH_MARGIN;
                const numLines = Math.ceil(labelWidth / MAX_DIM_WIDTH);
                if (numLines > 2 && variant !== "SparkChart") {
                  rowHeights.header = R.max(
                    R.defaultTo(0, rowHeights.header),
                    THREE_LINE_HEADER_TEXT_HEIGHT
                  );
                } else if (numLines > 1 && variant !== "SparkChart") {
                  rowHeights.header = R.max(
                    R.defaultTo(0, rowHeights.header),
                    TWO_LINE_HEADER_TEXT_HEIGHT
                  );
                } else {
                  rowHeights.header = R.max(R.defaultTo(0, rowHeights.header), HEADER_TEXT_HEIGHT);
                }
                if (leftColWidths[dimColIndex]) {
                  leftColWidths[dimColIndex] = R.min(
                    MAX_DIM_WIDTH,
                    R.max(leftColWidths[dimColIndex], labelWidth)
                  );
                } else {
                  leftColWidths[dimColIndex] = labelWidth;
                }
              }
              // Data Header Widths
              const dataHeader = R.defaultTo([], dataHeaders)[dataColIndex];
              const genericDataColumnMetaData = columnMetaDataMap[dataHeader.dataVarName];
              const dataHeaderLabel = R.defaultTo(
                R.defaultTo({}, genericDataColumnMetaData).displayName,
                dataHeader.label
              );
              if (dataHeaderLabel) {
                const labelWidth =
                  Math.ceil(getTextWidth(dataHeaderLabel, 14, 501)) + TEXT_WIDTH_MARGIN;
                const numLines = Math.ceil(labelWidth / MAX_DIM_WIDTH);
                if (numLines > 2 && variant !== "SparkChart") {
                  rowHeights.header = R.max(
                    R.defaultTo(0, rowHeights.header),
                    THREE_LINE_HEADER_TEXT_HEIGHT
                  );
                } else if (numLines > 1 && variant !== "SparkChart") {
                  rowHeights.header = R.max(
                    R.defaultTo(0, rowHeights.header),
                    TWO_LINE_HEADER_TEXT_HEIGHT
                  );
                } else {
                  rowHeights.header = R.max(R.defaultTo(0, rowHeights.header), HEADER_TEXT_HEIGHT);
                }
                if (colWidths[dataColIndex]) {
                  colWidths[dataColIndex] = R.min(
                    MAX_GRID_COL_WIDTH,
                    R.max(colWidths[dataColIndex], labelWidth)
                  );
                } else {
                  colWidths[dataColIndex] = labelWidth;
                }
              }
              // Data Footer Widths
              const aggregateDataLabel =
                aggregateData && aggregateData[dataColIndex] && aggregateData[dataColIndex].label
                  ? aggregateData[dataColIndex].label
                  : undefined;
              if (aggregateDataLabel) {
                const labelWidth = Math.max(
                  Math.ceil(getTextWidth(aggregateDataLabel, 14, 501)) + TEXT_WIDTH_MARGIN, // Add some padding to avoid wrapping
                  Math.ceil(getTextWidth("OVERALL", 14, 501))
                );
                if (colWidths[dataColIndex]) {
                  colWidths[dataColIndex] = R.min(
                    MAX_GRID_COL_WIDTH,
                    R.max(colWidths[dataColIndex], labelWidth)
                  );
                } else {
                  colWidths[dataColIndex] = labelWidth;
                }
              }
            }
            // Dimension Data Cells
            const dimHeader = R.defaultTo([], dimensionDataHeaders)[dimColIndex];
            // Include extra space for the scrollbar
            const genericColumnMetaData = columnMetaDataMap[dimHeader.dimensionTypeName];

            const isAdNameColumn = dimHeader.dimensionTypeName === "Ad Name";
            const thumbnailSize = isAdNameColumn ? selectedThumbnailSize : undefined;
            const thumbnailDimensions = thumbnailSize ? THUMBNAIL_SIZES[thumbnailSize] : undefined;

            const extraImageGapWidth = dimHeader && dimHeader.icon === "hasIcon" ? 14 : 0;
            const extraImageWidth =
              dimHeader && dimHeader.icon === "hasIcon"
                ? genericColumnMetaData.iconStyle === "thumbnail"
                  ? isAdNameColumn && thumbnailDimensions
                    ? thumbnailDimensions.width + extraImageGapWidth
                    : THUMBNAIL_WIDTH + extraImageGapWidth
                  : LOGO_WIDTH + extraImageGapWidth
                : 0;
            const { label: dimensionCellLabel } = sortedDimensionData[rowIndex][
              dimHeader.dimensionTypeName
            ];
            if (dimensionCellLabel) {
              const labelWidth =
                Math.ceil(getTextWidth(dimensionCellLabel, 14, 600)) + TEXT_WIDTH_MARGIN;
              const numLines = Math.ceil(labelWidth / MAX_DIM_WIDTH);
              // Set Row Heights
              if (
                dimHeader &&
                dimHeader.icon === "hasIcon" &&
                genericColumnMetaData.iconStyle === "thumbnail" &&
                isAdNameColumn &&
                thumbnailDimensions
              ) {
                const minHeightForThumbnail = thumbnailDimensions.height + 2 * ROW_PADDING;
                rowHeights[rowIndex] = R.max(
                  R.defaultTo(0, rowHeights[rowIndex]),
                  minHeightForThumbnail
                );
              } else if (
                (numLines > 2 || ["hasIcon", "iconOnly"].includes(dimHeader.icon as string)) &&
                variant !== "SparkChart"
              ) {
                rowHeights[rowIndex] = R.max(
                  R.defaultTo(0, rowHeights[rowIndex]),
                  THREE_LINE_ROW_HEIGHT + 2 * ROW_PADDING
                );
              } else if (numLines > 1 && variant !== "SparkChart") {
                rowHeights[rowIndex] = R.max(
                  R.defaultTo(0, rowHeights[rowIndex]),
                  TWO_LINE_ROW_HEIGHT + 2 * ROW_PADDING
                );
              } else {
                rowHeights[rowIndex] = R.max(
                  R.defaultTo(0, rowHeights[rowIndex]),
                  MIN_ROW_HEIGHT + 2 * ROW_PADDING
                );
              }
              // Set Column Widths
              if (dimHeader.icon === "iconOnly") {
                leftColWidths[dimColIndex] =
                  genericColumnMetaData.iconStyle === "thumbnail" ? THUMBNAIL_WIDTH : LOGO_WIDTH;
              } else if (leftColWidths[dimColIndex]) {
                leftColWidths[dimColIndex] = R.min(
                  MAX_DIM_WIDTH + extraImageWidth,
                  R.max(leftColWidths[dimColIndex], labelWidth + extraImageWidth)
                );
              } else {
                leftColWidths[dimColIndex] = labelWidth;
              }
            }
            // Table Data Cells
            const { label: dataCellLabel } = sortedTableData[rowIndex][dataColIndex];
            if (dataCellLabel) {
              const labelWidth =
                Math.ceil(getTextWidth(dataCellLabel, 14, 400)) + TEXT_WIDTH_MARGIN;
              const numLines = Math.ceil(labelWidth / MAX_GRID_COL_WIDTH);
              // Set Row Heights
              if (numLines > 2 && variant !== "SparkChart") {
                rowHeights[rowIndex] = R.max(
                  R.defaultTo(0, rowHeights[rowIndex]),
                  THREE_LINE_ROW_HEIGHT + 2 * ROW_PADDING
                );
              } else if (numLines > 1 && variant !== "SparkChart") {
                rowHeights[rowIndex] = R.max(
                  R.defaultTo(0, rowHeights[rowIndex]),
                  TWO_LINE_ROW_HEIGHT + 2 * ROW_PADDING
                );
              } else {
                rowHeights[rowIndex] = R.max(
                  R.defaultTo(0, rowHeights[rowIndex]),
                  MIN_ROW_HEIGHT + 2 * ROW_PADDING
                );
              }
              // Set Column Widths
              if (colWidths[dataColIndex]) {
                const extra = variant === "Metrics" ? 1 : 0;
                colWidths[dataColIndex] = R.min(
                  MAX_GRID_COL_WIDTH,
                  R.max(colWidths[dataColIndex] + extra, labelWidth)
                );
              } else {
                colWidths[dataColIndex] = labelWidth;
              }
              // Set Heatmapping Widths
              const heatMappingWidth = labelWidth + 28;
              if (heatMappingWidths[dataColIndex]) {
                heatMappingWidths[dataColIndex] = R.min(
                  MAX_GRID_COL_WIDTH,
                  R.max(heatMappingWidths[dataColIndex], heatMappingWidth)
                );
              } else {
                heatMappingWidths[dataColIndex] = heatMappingWidth;
              }
            }
          }
        }
      }
      if (!R.isNil(overrideDimensionColWidth)) {
        for (let colIndex = 0; colIndex < R.keys(leftColWidths).length; colIndex++) {
          const overrideWidth: number = Array.isArray(overrideDimensionColWidth)
            ? overrideDimensionColWidth[colIndex]
            : overrideDimensionColWidth;
          if (!isNaN(overrideWidth)) {
            colWidths[colIndex] = overrideWidth;
          }
        }
      }
      if (!R.isNil(overrideColWidth)) {
        for (let colIndex = 0; colIndex < R.keys(colWidths).length; colIndex++) {
          const overrideWidth = Array.isArray(overrideColWidth)
            ? overrideColWidth[colIndex]
            : overrideColWidth;
          if (!isNaN(overrideWidth)) {
            colWidths[colIndex] = overrideWidth;
          }
        }
      }
      for (const rowIndex of R.keys(rowHeights)) {
        if (!R.isNil(overrideRowHeight)) {
          rowHeights[rowIndex as string] = Array.isArray(overrideRowHeight)
            ? overrideRowHeight[rowIndex]
            : overrideRowHeight;
        }
      }
      return {
        leftColWidths: R.keys(leftColWidths).length ? leftColWidths : undefined,
        colWidths: R.keys(colWidths).length ? colWidths : undefined,
        heatMappingWidths: R.keys(heatMappingWidths).length ? heatMappingWidths : undefined,
        rowHeights: R.keys(rowHeights).length ? rowHeights : undefined,
      };
    }, [
      sortedDimensionData,
      sortedTableData,
      overrideDimensionColWidth,
      overrideColWidth,
      numRows,
      numDataCols,
      numDimCols,
      dimensionDataHeaders,
      columnMetaDataMap,
      dataHeaders,
      aggregateData,
      MAX_DIM_WIDTH,
      variant,
      HEADER_TEXT_HEIGHT,
      MAX_GRID_COL_WIDTH,
      ROW_PADDING,
      MIN_ROW_HEIGHT,
      overrideRowHeight,
      selectedThumbnailSize,
    ]);

    const leftWidth = useMemo(() => {
      let sum = LEFT_BORDER_WIDTH;
      R.map(colIndex => {
        if (leftColWidths && leftColWidths[colIndex]) {
          sum += leftColWidths[colIndex];
        }
        return colIndex;
      }, R.keys(leftColWidths));
      return hasDimensionData ? sum : 0;
    }, [hasDimensionData, leftColWidths]);

    const gridWidth = useMemo(() => {
      let sum = 0;
      R.map(colIndex => {
        if (colWidths && colWidths[colIndex]) {
          const columnWidth =
            colWidths && colWidths[colIndex]
              ? colWidths[colIndex]
              : MAX_GRID_COL_WIDTH + 2 * MAX_COL_PADDING;
          const borderWidth =
            dataHeaders && dataHeaders[colIndex] && dataHeaders[colIndex].divider
              ? 2 * COL_DIVIDER_BORDER_WIDTH
              : parseInt(colIndex) === R.keys(colWidths).length - 1
              ? 0.25
              : 0.5;
          sum += columnWidth + borderWidth;
        }
        return colIndex;
      }, R.keys(colWidths));
      return sum;
    }, [MAX_COL_PADDING, MAX_GRID_COL_WIDTH, colWidths, dataHeaders]);

    const cellPadding = useMemo(() => {
      if (!R.isNil(overrideColPadding)) {
        return overrideColPadding;
      }
      if (leftWidth && hasDimensionData && dataHeaders) {
        const remainingWidth =
          width - leftWidth - gridWidth - TABLE_RIGHT_PADDING - DIMENSION_COLUMNS_RIGHT_MARGIN;
        const cellPadding = R.min(
          MAX_COL_PADDING,
          R.max(MIN_COL_PADDING, Math.floor(remainingWidth / (2 * numCols)))
        );
        return cellPadding;
      }
      return MIN_COL_PADDING;
    }, [
      overrideColPadding,
      leftWidth,
      hasDimensionData,
      dataHeaders,
      MIN_COL_PADDING,
      width,
      gridWidth,
      TABLE_RIGHT_PADDING,
      DIMENSION_COLUMNS_RIGHT_MARGIN,
      MAX_COL_PADDING,
      numCols,
    ]);

    const columnWidths = useCallback(
      colIndex => {
        const columnWidth =
          colWidths && colWidths[colIndex]
            ? colWidths[colIndex] + 2 * cellPadding
            : MAX_GRID_COL_WIDTH + 2 * MAX_COL_PADDING;
        const borderWidth =
          dataHeaders && dataHeaders[colIndex] && dataHeaders[colIndex].divider
            ? 2 * COL_DIVIDER_BORDER_WIDTH
            : 0.25;
        if (!R.isNil(overrideColPadding) || !R.isNil(overrideColWidth)) {
          const overrideWidth = Array.isArray(overrideColWidth)
            ? overrideColWidth[colIndex]
            : overrideColWidth;
          return R.defaultTo(columnWidth, overrideWidth) + borderWidth;
        }
        return (
          columnWidth +
          borderWidth +
          (colIndex === 0 ? DIMENSION_COLUMNS_RIGHT_MARGIN : 0) +
          (colIndex === numDataCols - 1 ? TABLE_RIGHT_PADDING : 0)
        );
      },
      [
        colWidths,
        cellPadding,
        MAX_GRID_COL_WIDTH,
        MAX_COL_PADDING,
        dataHeaders,
        overrideColPadding,
        overrideColWidth,
        DIMENSION_COLUMNS_RIGHT_MARGIN,
        numDataCols,
        TABLE_RIGHT_PADDING,
      ]
    );

    const hasSubHeader = useMemo(() => {
      if (Boolean(percentAOIPlaceholder) || percentAOIPlaceholder === 0) {
        return true;
      }
      if (dataHeaders) {
        return R.any(({ subHeader }) => Boolean(subHeader), dataHeaders);
      }
    }, [dataHeaders, percentAOIPlaceholder]);

    const headerContainerHeight = useMemo(
      () =>
        hasDataHeaders
          ? R.max(
              rowHeights && rowHeights.header
                ? rowHeights.header + (hasSubHeader ? HEADER_SUBHEADER_GAP + SUB_HEADER_HEIGHT : 0)
                : MIN_HEADER_CONTAINER_HEIGHT,
              MIN_HEADER_CONTAINER_HEIGHT
            )
          : 0,
      [hasDataHeaders, hasSubHeader, rowHeights]
    );

    const dividerLines = useMemo(() => {
      const dividerLines = {};
      if (dataHeaders) {
        for (let i = 0; i < numDataCols; i++) {
          // If we are on the first column, ignore since we already have a dimension divider
          const dividerKey = i === 0 ? undefined : `${i - 1}_${i}`;
          if (dataHeaders[i].divider && dividerKey) {
            dividerLines[dividerKey] = true;
          }
        }
      }
      if (superHeaders) {
        let leftColumnIndex = 0;
        for (let i = 0; i < superHeaders.length; i++) {
          const rightColumnIndex = leftColumnIndex + superHeaders[i].span - 1;
          const superHeader = superHeaders[i];
          const leftDividerKey =
            leftColumnIndex === 0 ? undefined : `${leftColumnIndex - 1}_${leftColumnIndex}`;
          const rightDividerKey =
            rightColumnIndex === numDataCols - 1
              ? rightColumnIndex
              : `${rightColumnIndex}_${rightColumnIndex + 1}`;
          if (superHeader.data.leftDivider && leftDividerKey) {
            dividerLines[leftDividerKey] = true;
          }
          if (superHeader.data.rightDivider) {
            dividerLines[rightDividerKey] = true;
          }
          leftColumnIndex += superHeader.span;
        }
      }
      return dividerLines;
    }, [dataHeaders, numDataCols, superHeaders]);

    const rowHeight = useCallback(
      rowIndex => {
        return rowHeights && rowHeights[rowIndex] ? rowHeights[rowIndex] : MIN_ROW_HEIGHT;
      },
      [MIN_ROW_HEIGHT, rowHeights]
    );

    const dataHeaderRenderer: CellRenderer<Column> = useMemo(
      () => ({ columnIndex, classes = [], data, style = {} }: RenderProps) => {
        const { label: columnLabel, subHeader } = data as Column;
        const header = dataHeaders[columnIndex];
        const overlayText =
          columnMetaDataMap && columnMetaDataMap[header.dataVarName]
            ? columnMetaDataMap[header.dataVarName].customMetaData
              ? columnMetaDataMap[header.dataVarName].customMetaData.description
              : columnMetaDataMap[header.dataVarName].overlayText
            : undefined;
        const overlayPlacement =
          columnMetaDataMap && columnMetaDataMap[header.dataVarName]
            ? columnMetaDataMap[header.dataVarName].overlayPlacement
            : undefined;
        const mySorting = R.find(({ id }) => id === header.id, sorting);
        const contentType =
          !R.isNil(columnIndex) &&
          header &&
          header.dataVarName &&
          columnMetaDataMap[header.dataVarName] &&
          columnMetaDataMap[header.dataVarName].contentType
            ? columnMetaDataMap[header.dataVarName].contentType
            : "number";
        const alignItems = contentType === "text" ? "flex-start" : "flex-end";
        const headerHeight = R.max(
          HEADER_TEXT_HEIGHT,
          rowHeights && rowHeights.header ? rowHeights.header : HEADER_TEXT_HEIGHT
        );
        const justifyContent = alignItems;
        const maxWidth =
          MAX_GRID_COL_WIDTH +
          cellPadding * 2 +
          COL_DIVIDER_BORDER_WIDTH +
          (columnIndex === 0 ? DIMENSION_COLUMNS_RIGHT_MARGIN : 0);
        const minHeight = MIN_ROW_HEIGHT;
        const paddingBottom = ROW_PADDING;
        const paddingLeft = cellPadding + (columnIndex === 0 ? DIMENSION_COLUMNS_RIGHT_MARGIN : 0);
        const paddingRight =
          cellPadding + (columnIndex === numDataCols - 1 ? TABLE_RIGHT_PADDING : 0);
        const paddingTop = ROW_PADDING;
        const subHeaderHeight = SUB_HEADER_HEIGHT;
        const textAlign = contentType === "text" ? "left" : "right";
        const label = columnLabel
          ? columnLabel
          : columnMetaDataMap[header.dataVarName]
          ? columnMetaDataMap[header.dataVarName].displayName
          : header.dataVarName;
        const headerText = (
          <div
            className={`text ${TEXT_SIZE_MAP[variant]}`}
            style={{
              alignItems: "center",
              display: "flex",
              height: headerHeight,
              textAlign,
              width: "fit-content",
            }}
          >
            {label}
          </div>
        );
        // We want to maintain the same spacing if there is just one subheader in the table,
        // so we add a placeholder subheader if there isn't one but make the text transparent
        const subHeaderText = subHeader && (
          <div
            className="subHeader text"
            style={{
              color: subHeader ? "$Brand100" : "transparent",
              height: subHeaderHeight,
              justifyContent,
              textAlign,
              width: "fit-content",
            }}
          >
            {subHeader}
          </div>
        );
        const sortIconSide = contentType === "text" ? "right" : "left";
        const sortIcon = contentType !== "logo" && (
          <div className={`sortIconContainer ${R.isNil(mySorting) ? "" : "sorted"}`}>
            {mySorting && mySorting.asc ? <MdArrowUpward /> : <MdArrowDownward />}
          </div>
        );
        const headerContainerJustifyContent = subHeader ? "space-between" : "flex-end";
        const cursor = contentType !== "logo" ? "pointer" : "default";
        return innerCellRenderer(
          <div
            style={{
              ...style,
              alignItems,
              height: headerContainerHeight,
              justifyContent: headerContainerJustifyContent,
              maxWidth,
              minHeight,
              paddingBottom,
              paddingLeft,
              paddingRight,
              paddingTop,
              textAlign,
            }}
            className={cn(...classes, "tableHeaderContainer", {
              end: columnIndex === numDataCols - 1,
              leftDivider: dividerLines[`${columnIndex - 1}_${columnIndex}`],
              rightDivider: dividerLines[`${columnIndex}_${columnIndex + 1}`],
              rightEndDivider: dividerLines[columnIndex],
            })}
          >
            <div
              className="tableHeader"
              onClick={() => {
                if (contentType !== "logo") {
                  addSorting(header.id, disableMultiSorting);
                }
              }}
              style={{
                alignItems,
                cursor,
                justifyContent,
                textAlign,
              }}
            >
              {sortIconSide === "right" ? headerText : sortIcon}
              {sortIconSide === "right" ? sortIcon : headerText}
            </div>
            {(Boolean(percentAOIPlaceholder) || percentAOIPlaceholder === 0) &&
              header.dataVarName.toLowerCase().includes("percentincremental") && (
                <div
                  style={{
                    width: "100%",
                  }}
                >
                  <PercentAOIPlaceholder percentage={percentAOIPlaceholder as number} />
                </div>
              )}
            {hasSubHeader && subHeaderText}
          </div>,
          overlayText,
          overlayPlacement
        );
      },
      [
        dataHeaders,
        sorting,
        rowHeights,
        MAX_GRID_COL_WIDTH,
        cellPadding,
        MIN_ROW_HEIGHT,
        ROW_PADDING,
        DIMENSION_COLUMNS_RIGHT_MARGIN,
        numDataCols,
        TABLE_RIGHT_PADDING,
        columnMetaDataMap,
        variant,
        headerContainerHeight,
        dividerLines,
        percentAOIPlaceholder,
        hasSubHeader,
        addSorting,
        disableMultiSorting,
        HEADER_TEXT_HEIGHT,
      ]
    );

    const cellRenderer: CellRenderer<CellData> = useMemo(
      () => ({
        classes = [],
        columnIndex = 0,
        data,
        rowIndex = 0,
        style = {},
        virtual,
      }: RenderProps) => {
        const { label, overlayText, value } = data as CellData;
        const ourClasses = [...classes, "bodyCell"];
        if (columnIndex === 0) {
          ourClasses.push("start");
        }
        if (columnIndex === numDataCols - 1) {
          ourClasses.push("end");
        }
        if (rowIndex === numRows - 1) {
          ourClasses.push("lastRow");
        }
        if (dividerLines[`${columnIndex - 1}_${columnIndex}`]) {
          ourClasses.push("leftDivider");
        }
        if (dividerLines[`${columnIndex}_${columnIndex + 1}`]) {
          ourClasses.push("rightDivider");
        }
        if (dividerLines[columnIndex]) {
          ourClasses.push("rightEndDivider");
        }
        const { backgroundColor, outline } =
          typeof value === "number"
            ? getHeatMapInfo({
                columnIndex,
                value: value,
              })
            : { backgroundColor: "transparent", outline: "none" };
        const header = dataHeaders[columnIndex];
        const contentType =
          !R.isNil(columnIndex) &&
          header &&
          header.dataVarName &&
          columnMetaDataMap[header.dataVarName] &&
          columnMetaDataMap[header.dataVarName].contentType
            ? columnMetaDataMap[header.dataVarName].contentType
            : "number";
        const height =
          colWidths &&
          colWidths[columnIndex] &&
          rowHeights &&
          (rowIndex || rowIndex === 0) &&
          rowHeights[rowIndex]
            ? rowHeights[rowIndex] - 2 * ROW_PADDING
            : "fit-content";
        const justifyContent =
          contentType === "text" && backgroundColor !== "transparent" ? "flex-start" : "flex-end";
        const maxWidth =
          MAX_GRID_COL_WIDTH +
          cellPadding * 2 +
          COL_DIVIDER_BORDER_WIDTH +
          (columnIndex === 0 ? DIMENSION_COLUMNS_RIGHT_MARGIN : 0);
        const minHeight = MIN_ROW_HEIGHT;
        // Padding in between cells (column,row)
        const paddingLeft = cellPadding + (columnIndex === 0 ? DIMENSION_COLUMNS_RIGHT_MARGIN : 0);
        const paddingRight =
          cellPadding + (columnIndex === numDataCols - 1 ? TABLE_RIGHT_PADDING : 0);
        // We need some extra padding on the first row to account for the border
        const bodyCellPaddingTop =
          rowIndex === 0 ? HEADER_FOOTER_PADDING + ROW_PADDING : ROW_PADDING;
        const bodyCellPaddingBottom =
          rowIndex === numRows - 1 ? HEADER_FOOTER_PADDING + ROW_PADDING : ROW_PADDING;
        // Inner Cell Bottom/Top Padding inside of the background color
        const paddingBottom =
          variant !== "SparkChart" ? (MIN_ROW_HEIGHT - TEXT_FONT_SIZE_MAP[variant]) / 2 : 0;
        const paddingTop = paddingBottom;
        const textAlign =
          contentType === "text" && backgroundColor !== "transparent" ? "left" : "right";
        const width =
          heatMappingWidths && heatMappingWidths[columnIndex]
            ? heatMappingWidths[columnIndex]
            : "fit-content";
        let bodyCellStyling = {
          ...style,
          justifyContent,
          maxWidth,
          paddingBottom: bodyCellPaddingBottom,
          paddingLeft,
          paddingRight,
          paddingTop: bodyCellPaddingTop,
        };
        return (
          <div style={bodyCellStyling} className={ourClasses.join(" ")}>
            <div
              className="cellWrapper"
              style={{
                backgroundColor,
                height,
                justifyContent,
                outline,
                width,
              }}
            >
              {innerCellRenderer(
                <div
                  className={`innerCell ${contentType !== "logo" ? "text" : ""} ${
                    TEXT_SIZE_MAP[variant]
                  } ${backgroundColor !== "transparent" || outline !== "none" ? "heatMapped" : ""}`}
                  style={{
                    justifyContent,
                    minHeight,
                    paddingBottom,
                    paddingTop,
                    textAlign,
                    width,
                  }}
                >
                  {label}
                </div>,
                overlayText
              )}
            </div>
          </div>
        );
      },
      [
        cellPadding,
        columnMetaDataMap,
        colWidths,
        dataHeaders,
        DIMENSION_COLUMNS_RIGHT_MARGIN,
        dividerLines,
        getHeatMapInfo,
        HEADER_FOOTER_PADDING,
        heatMappingWidths,
        MAX_GRID_COL_WIDTH,
        MIN_ROW_HEIGHT,
        numDataCols,
        numRows,
        ROW_PADDING,
        rowHeights,
        TABLE_RIGHT_PADDING,
        variant,
      ]
    );

    const dimensionHeaderRenderer = useMemo(
      () => (corner: CornerLocation) => {
        // Render Dimension Columns
        if (corner === "nw") {
          const classes = ["dimensionCornerContainer"];
          if (superHeaders) {
            classes.push("withSuperHeaders");
          }
          let columnIndex = 0;
          const height = headerContainerHeight;
          return (
            <div className={classes.join(" ")} style={{ height }}>
              {R.map(dimension => {
                const classes = ["tableHeader"];
                const mySorting = R.find(({ id }) => id === dimension.id, sorting);
                const dimHeader = dimensionDataHeaders[columnIndex];
                const overlayText =
                  dimHeader && columnMetaDataMap[dimHeader.dimensionTypeName]
                    ? columnMetaDataMap[dimHeader.dimensionTypeName].overlayText
                    : undefined;
                const contentType =
                  dimHeader &&
                  columnMetaDataMap[dimHeader.dimensionTypeName] &&
                  columnMetaDataMap[dimHeader.dimensionTypeName].contentType
                    ? columnMetaDataMap[dimHeader.dimensionTypeName].contentType
                    : "text";
                const iconStyle =
                  dimHeader &&
                  columnMetaDataMap[dimHeader.dimensionTypeName] &&
                  columnMetaDataMap[dimHeader.dimensionTypeName].iconStyle
                    ? columnMetaDataMap[dimHeader.dimensionTypeName].iconStyle
                    : undefined;
                const sortIconSide =
                  contentType === "text" ? "right" : contentType === "logo" ? "center" : "left";
                const justifyContent =
                  contentType === "text" || contentType === "logo" ? "flex-start" : "flex-end";
                const textAlign =
                  contentType === "text" || contentType === "logo" ? "left" : "right";
                const headerHeight = R.max(
                  rowHeights && rowHeights.header ? rowHeights.header : HEADER_TEXT_HEIGHT,
                  HEADER_TEXT_HEIGHT
                );
                const label = dimension.label
                  ? dimension.label
                  : columnMetaDataMap[dimension.dimensionTypeName]
                  ? columnMetaDataMap[dimension.dimensionTypeName].displayName
                  : dimension.dimensionTypeName;
                const headerText =
                  dimension.dimensionTypeName === "Ad Name" ? (
                    <div
                      className="tableHeaderLabel"
                      style={{
                        alignItems: "center",
                        display: "flex",
                        height: headerHeight,
                        textAlign,
                        gap: "16px",
                      }}
                    >
                      <Dropdown
                        type={DropdownToggleType.EMPTY}
                        options={[
                          {
                            label: " ",
                            value: "small",
                            customContent: (
                              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                <svg
                                  width="22"
                                  height="22"
                                  viewBox="0 0 22 22"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M19.5 2C19.7761 2 20 2.22386 20 2.5V4.5C20 4.77614 19.7761 5 19.5 5H2.5C2.22386 5 2 4.77614 2 4.5V2.5C2 2.22386 2.22386 2 2.5 2H19.5Z"
                                    fill="black"
                                  />
                                  <path
                                    d="M19.5 7C19.7761 7 20 7.22386 20 7.5V9.5C20 9.77614 19.7761 10 19.5 10H2.5C2.22386 10 2 9.77614 2 9.5V7.5C2 7.22386 2.22386 7 2.5 7H19.5Z"
                                    fill="black"
                                  />
                                  <path
                                    d="M19.5 12C19.7761 12 20 12.2239 20 12.5V14.5C20 14.7761 19.7761 15 19.5 15H2.5C2.22386 15 2 14.7761 2 14.5V12.5C2 12.2239 2.22386 12 2.5 12H19.5Z"
                                    fill="black"
                                  />
                                  <path
                                    d="M19.5 17C19.7761 17 20 17.2239 20 17.5V19.5C20 19.7761 19.7761 20 19.5 20H2.5C2.22386 20 2 19.7761 2 19.5V17.5C2 17.2239 2.22386 17 2.5 17H19.5Z"
                                    fill="black"
                                  />
                                </svg>
                                <span>Small</span>
                              </div>
                            ),
                          },
                          {
                            label: "  ",
                            value: "medium",
                            customContent: (
                              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                <svg
                                  width="22"
                                  height="22"
                                  viewBox="0 0 22 22"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M19.5 2C19.7761 2 20 2.22386 20 2.5V6.16667C20 6.44281 19.7761 6.66667 19.5 6.66667H2.5C2.22386 6.66667 2 6.44281 2 6.16667V2.5C2 2.22386 2.22386 2 2.5 2H19.5Z"
                                    fill="black"
                                  />
                                  <path
                                    d="M19.5 8.66699C19.7761 8.66699 20 8.89085 20 9.16699V12.8337C20 13.1098 19.7761 13.3337 19.5 13.3337H2.5C2.22386 13.3337 2 13.1098 2 12.8337V9.16699C2 8.89085 2.22386 8.66699 2.5 8.66699H19.5Z"
                                    fill="black"
                                  />
                                  <path
                                    d="M19.5 15.333C19.7761 15.333 20 15.5569 20 15.833V19.4997C20 19.7758 19.7761 19.9997 19.5 19.9997H2.5C2.22386 19.9997 2 19.7758 2 19.4997V15.833C2 15.5569 2.22386 15.333 2.5 15.333H19.5Z"
                                    fill="black"
                                  />
                                </svg>
                                <span>Medium</span>
                              </div>
                            ),
                          },
                          {
                            label: "   ",
                            value: "large",
                            customContent: (
                              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                <svg
                                  width="22"
                                  height="22"
                                  viewBox="0 0 22 22"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M19.5 2C19.7761 2 20 2.22386 20 2.5V9.5C20 9.77614 19.7761 10 19.5 10H2.5C2.22386 10 2 9.77614 2 9.5V2.5C2 2.22386 2.22386 2 2.5 2H19.5Z"
                                    fill="black"
                                  />
                                  <path
                                    d="M19.5 12C19.7761 12 20 12.2239 20 12.5V19.5C20 19.7761 19.7761 20 19.5 20H2.5C2.22386 20 2 19.7761 2 19.5V12.5C2 12.2239 2.22386 12 2.5 12H19.5Z"
                                    fill="black"
                                  />
                                </svg>
                                <span>Large</span>
                              </div>
                            ),
                          },
                        ]}
                        onChange={value => {
                          setSelectedThumbnailSize(value);
                        }}
                        leadingIcon={
                          <svg
                            width="18"
                            height="18"
                            viewBox="0 0 18 18"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M15.0534 2.35742C15.3788 2.35742 15.6426 2.62125 15.6426 2.94671V3.32171C15.6426 3.64716 15.3788 3.91099 15.0534 3.91099H2.94622C2.62077 3.91099 2.35693 3.64716 2.35693 3.32171V2.94671C2.35693 2.62125 2.62077 2.35742 2.94622 2.35742H15.0534Z"
                              fill="#1F003F"
                            />
                            <path
                              d="M15.0534 6.26758C15.3788 6.26758 15.6426 6.53141 15.6426 6.85686V7.23186C15.6426 7.55732 15.3788 7.82115 15.0534 7.82115H2.94622C2.62077 7.82115 2.35693 7.55732 2.35693 7.23186V6.85686C2.35693 6.53141 2.62077 6.26758 2.94622 6.26758H15.0534Z"
                              fill="#1F003F"
                            />
                            <path
                              d="M15.0534 10.1787C15.3788 10.1787 15.6426 10.4425 15.6426 10.768V11.143C15.6426 11.4684 15.3788 11.7323 15.0534 11.7323H2.94622C2.62077 11.7323 2.35693 11.4685 2.35693 11.143V10.768C2.35693 10.4425 2.62077 10.1787 2.94622 10.1787H15.0534Z"
                              fill="#1F003F"
                            />
                            <path
                              d="M15.0534 14.0889C15.3788 14.0889 15.6426 14.3527 15.6426 14.6782V15.0532C15.6426 15.3786 15.3788 15.6424 15.0534 15.6424H2.94622C2.62077 15.6424 2.35693 15.3786 2.35693 15.0532V14.6782C2.35693 14.3527 2.62077 14.0889 2.94622 14.0889H15.0534Z"
                              fill="#1F003F"
                            />
                          </svg>
                        }
                        size="sm"
                        overlay={<Tooltip id={"iconSize"}>{"Icon Size"}</Tooltip>}
                        className="ad-name-header-dropdown"
                      />
                      <span>{label}</span>
                    </div>
                  ) : (
                    <div
                      className="tableHeaderLabel"
                      style={{
                        alignItems: "center",
                        display: "flex",
                        height: headerHeight,
                        padding: "4px 0px",
                        textAlign,
                      }}
                    >
                      {label}
                    </div>
                  );
                const headerContainerJustifyContent = "flex-end";
                const extraImageWidth =
                  dimHeader && dimHeader.icon === "hasIcon" && iconStyle
                    ? (iconStyle === "logo" ? 50 : 90) + 14
                    : 0;
                const maxContainerWidth = MAX_DIM_WIDTH + 2 * MAX_COL_PADDING + extraImageWidth;
                const padding = `${ROW_PADDING}px ${cellPadding}px`;
                const width =
                  leftColWidths && leftColWidths[columnIndex]
                    ? leftColWidths[columnIndex]
                    : "fit-content";
                const sortIcon = contentType !== "logo" && (
                  <div className={`sortIconContainer ${R.isNil(mySorting) ? "" : "sorted"}`}>
                    {mySorting && mySorting.asc ? <MdArrowUpward /> : <MdArrowDownward />}
                  </div>
                );
                const cursor = contentType !== "logo" ? "pointer" : "default";
                const header = innerCellRenderer(
                  <div
                    key={`left_header_col${columnIndex}`}
                    className="tableHeaderContainer"
                    style={{
                      height,
                      justifyContent: headerContainerJustifyContent,
                      maxWidth: maxContainerWidth,
                      padding,
                    }}
                  >
                    <div
                      className={classes.join(" ")}
                      onClick={event => {
                        const target = event.target as Element;
                        const isDropdownClick =
                          target.closest(".ad-name-header-dropdown") ||
                          target.closest(".dropdownMenuItem") ||
                          target.closest(".dropdownMenuContainer");
                        if (contentType !== "logo" && !isDropdownClick) {
                          addSorting(dimension.id, disableMultiSorting);
                        }
                      }}
                      style={{
                        cursor,
                        justifyContent,
                        width,
                      }}
                    >
                      {sortIconSide === "right" ? headerText : sortIcon}
                      {sortIconSide === "right" ? sortIcon : headerText}
                    </div>
                  </div>,
                  overlayText
                );
                columnIndex++;
                return header;
              }, dimensionDataHeaders)}
            </div>
          );
        } else {
          return <div />; // We aren't rendering anything else for the other corners
        }
      },
      [
        superHeaders,
        headerContainerHeight,
        dimensionDataHeaders,
        sorting,
        rowHeights,
        columnMetaDataMap,
        MAX_DIM_WIDTH,
        MAX_COL_PADDING,
        ROW_PADDING,
        cellPadding,
        leftColWidths,
        addSorting,
        disableMultiSorting,
        HEADER_TEXT_HEIGHT,
      ]
    );

    const dimensionCellRenderer: CellRenderer<Record<string, CellData>> = useMemo(
      () => ({ classes = [], data, rowIndex = 0, style = {}, virtual }: RenderProps) => {
        const columns: JSX.Element[] = [];
        const alignItems = "center";
        let colIndex = 0;
        for (const header of dimensionDataHeaders) {
          const {
            content: contentOverride,
            label,
            overlayText,
            url: imageURL,
            adInfo,
          } = (data as Record<string, CellData>)[header.dimensionTypeName];
          const altText = overlayText !== label ? overlayText : "";
          const genericColumnMetaData =
            header && columnMetaDataMap && columnMetaDataMap[header.dimensionTypeName]
              ? columnMetaDataMap[header.dimensionTypeName]
              : undefined;
          const iconStyle =
            header && genericColumnMetaData && genericColumnMetaData.iconStyle
              ? genericColumnMetaData.iconStyle
              : undefined;
          const url = iconStyle ? R.defaultTo("", imageURL) : imageURL;
          const thumbnailSize = iconStyle === "thumbnail" ? selectedThumbnailSize : undefined;
          const contentType =
            header && iconStyle && header.icon === "iconOnly"
              ? iconStyle
              : header && iconStyle && header.icon === "hasIcon" && url !== ""
              ? `text ${iconStyle} ${iconStyle}-${thumbnailSize}`
              : header && genericColumnMetaData && genericColumnMetaData.contentType
              ? genericColumnMetaData.contentType
              : "text";
          const height =
            leftColWidths &&
            leftColWidths[colIndex] &&
            rowHeights &&
            (rowIndex || rowIndex === 0) &&
            rowHeights[rowIndex]
              ? rowHeights[rowIndex]
              : "fit-content";
          const justifyContent = contentType.includes("text")
            ? "flex-start"
            : contentType === "logo"
            ? "center"
            : "flex-end";
          const maxHeight =
            leftColWidths &&
            leftColWidths[colIndex] &&
            rowHeights &&
            (rowIndex || rowIndex === 0) &&
            rowHeights[rowIndex]
              ? `${rowHeights[rowIndex]} !important`
              : THREE_LINE_ROW_HEIGHT + 2 * ROW_PADDING;
          const extraImageWidth =
            header && header.icon === "hasIcon" && iconStyle
              ? (iconStyle === "logo" ? 50 : 90) + 14
              : 0;
          const maxWidth = MAX_DIM_WIDTH + 2 * cellPadding + extraImageWidth;
          // Padding in between cells (column,row)
          const padding = `${ROW_PADDING}px ${cellPadding}px`;
          // Inner Cell Bottom/Top Padding inside of the background color
          const paddingBottom =
            variant !== "SparkChart" ? (MIN_ROW_HEIGHT - TEXT_FONT_SIZE_MAP[variant]) / 2 : 0;
          const paddingTop = paddingBottom;
          const textAlign = contentType.includes("text")
            ? "left"
            : contentType === "logo"
            ? "center"
            : "right";
          const width =
            leftColWidths && leftColWidths[colIndex] ? leftColWidths[colIndex] : "fit-content";
          const content = !R.isNil(contentOverride) ? (
            contentOverride
          ) : !R.isNil(url) && header.icon === "iconOnly" ? (
            innerCellRenderer(<Img src={url} alt={altText} unloader={altText} />, overlayText)
          ) : !R.isNil(url) && url !== "" && header.icon ? (
            <div className="dimensionCellWithIcon">
              {innerCellRenderer(
                <div className={iconStyle}>
                  {<Img src={url} alt={altText} unloader={altText} />}
                </div>,
                overlayText
              )}
              <div className={`label ${leftColWidths && leftColWidths[colIndex] ? "" : "noWrap"}`}>
                {label}
              </div>
            </div>
          ) : (
            innerCellRenderer(
              <div className={`label ${leftColWidths && leftColWidths[colIndex] ? "" : "noWrap"}`}>
                {label}
              </div>,
              overlayText
            )
          );

          const isAdNameColumn = header.dimensionTypeName === "Ad Name";
          const handleClick =
            isAdNameColumn && shouldEnableMotionReplacement
              ? () => {
                  setCreativeTrayOpen(true);
                  if (adInfo) {
                    setAdInfo(adInfo);
                  } else {
                    setAdInfo({
                      company: "",
                      platform: "",
                      ad_name: "",
                      ad_id: "",
                      account_id: "",
                    });
                  }
                  const displayedMetrics = displayedMetricsMap[label];
                  if (displayedMetrics) {
                    setCreativeTrayMetrics(displayedMetrics);
                  }
                }
              : undefined;
          const cursor = isAdNameColumn && shouldEnableMotionReplacement ? "pointer" : "default";
          columns.push(
            <div
              key={`left_col${colIndex}_row${rowIndex}`}
              className="cellWrapper"
              style={{
                alignItems,
                height,
                maxHeight,
                maxWidth,
                padding,
              }}
            >
              <div
                className={`innerCell ${contentType} ${TEXT_SIZE_MAP[variant]}`}
                style={{
                  justifyContent,
                  paddingBottom,
                  paddingTop,
                  textAlign,
                  width,
                  cursor,
                }}
                onClick={handleClick}
              >
                {content}
              </div>
            </div>
          );
          colIndex += 1;
        }
        const paddingTop = rowIndex === 0 ? HEADER_FOOTER_PADDING + ROW_PADDING : ROW_PADDING;
        const paddingBottom =
          rowIndex === numRows - 1 ? HEADER_FOOTER_PADDING + ROW_PADDING : ROW_PADDING;
        const height =
          (rowHeights ? rowHeight(rowIndex) : MIN_ROW_HEIGHT) +
          (rowIndex === 0 || rowIndex === numRows - 1 ? HEADER_FOOTER_PADDING : 0);
        const maxHeight = rowHeights ? rowHeight(rowIndex) : MIN_ROW_HEIGHT;
        const minHeight = MIN_ROW_HEIGHT;
        if (rowIndex === numRows - 1) {
          classes.push("lastRow");
        }
        return (
          <div
            style={{
              ...style,
              height,
              maxHeight,
              minHeight,
              paddingBottom,
              paddingTop,
            }}
            className={[...classes, "leftCell"].join(" ")}
          >
            {columns}
          </div>
        );
      },
      [
        cellPadding,
        columnMetaDataMap,
        dimensionDataHeaders,
        displayedMetricsMap,
        HEADER_FOOTER_PADDING,
        leftColWidths,
        MAX_DIM_WIDTH,
        MIN_ROW_HEIGHT,
        numRows,
        ROW_PADDING,
        rowHeight,
        rowHeights,
        variant,
        setCreativeTrayOpen,
        shouldEnableMotionReplacement,
        selectedThumbnailSize,
      ]
    );

    const aggregateDataRenderer: CellRenderer<CellData> = useMemo(
      () => ({ classes = [], columnIndex, data, style = {} }: RenderProps) => {
        const { label } = data as CellData;
        const ourClasses = [...classes, "subtotalCell", "bodyCell"];
        if (columnIndex === 0) {
          ourClasses.push("start");
        }
        if (columnIndex === numDataCols - 1) {
          ourClasses.push("end");
        }
        const dataHeader =
          dataHeaders && !R.isNil(columnIndex) ? dataHeaders[columnIndex] : undefined;
        const contentType =
          !R.isNil(columnIndex) &&
          dataHeader &&
          dataHeader.dataVarName &&
          columnMetaDataMap[dataHeader.dataVarName] &&
          columnMetaDataMap[dataHeader.dataVarName].contentType
            ? columnMetaDataMap[dataHeader.dataVarName].contentType
            : "number";
        const justifyContent =
          contentType === "text" ? "flex-start" : contentType === "logo" ? "center" : "flex-end";
        const containerAlignItems = "flex-start";
        const maxWidth = MAX_GRID_COL_WIDTH;
        const minHeight = MIN_ROW_HEIGHT;
        const paddingBottom = FOOTER_PADDING;
        const paddingLeft = cellPadding + (columnIndex === 0 ? DIMENSION_COLUMNS_RIGHT_MARGIN : 0);
        const paddingRight =
          cellPadding + (columnIndex === numDataCols - 1 ? TABLE_RIGHT_PADDING : 0);
        const paddingTop = FOOTER_PADDING;
        const textAlign =
          contentType === "text" ? "left" : contentType === "logo" ? "center" : "right";
        const footerTextWidth = "fit-content";
        const width = colWidths && colWidths[columnIndex] ? colWidths[columnIndex] : "fit-content";
        const height = FOOTER_HEIGHT;
        const columnMetaData: ColumnMetaData | undefined =
          columnMetaDataMap && dataHeader && dataHeader.dataVarName
            ? columnMetaDataMap[dataHeader.dataVarName]
            : undefined;
        let usingAverage = false;
        if (columnMetaData && columnMetaData.requiredTotalsColumns) {
          for (const column of columnMetaData.requiredTotalsColumns) {
            if (
              column === "numRows" ||
              (typeof column === "object" &&
                column.dataVarName &&
                column.dataVarName.toLowerCase().includes("weighted"))
            ) {
              usingAverage = true;
            }
          }
        }
        let footerType = "TOTAL";
        if (dataHeader && dataHeader.footerLabel) {
          footerType = dataHeader.footerLabel;
        } else if (usingAverage) {
          footerType = "AVG";
        } else if (columnMetaData && columnMetaData.customMetaData) {
          for (const operator of [...OPERATORS, ...R.map(fn => `${fn}(`, FUNCTIONS)]) {
            if (
              columnMetaData.customMetaData.function_string.includes(operator) &&
              operator !== "-" &&
              operator !== "+"
            ) {
              footerType = "OVERALL";
            }
          }
        } else if (columnMetaData && !R.isNil(columnMetaData.aggregator)) {
          footerType = "OVERALL";
        }
        return (
          <div
            style={{
              ...style,
              alignItems: containerAlignItems,
              height,
              paddingLeft,
              paddingRight,
            }}
            className={ourClasses.join(" ")}
          >
            <div
              className="cellWrapper"
              style={{
                alignItems: containerAlignItems,
                height,
                justifyContent,
                maxWidth,
                minHeight,
                paddingBottom: FOOTER_CONTAINER_BOTTOM_PADDING,
                paddingTop: FOOTER_CONTAINER_TOP_PADDING,
              }}
            >
              <div
                className={`footer innerCell ${contentType !== "logo" ? "text" : ""} ${
                  TEXT_SIZE_MAP[variant]
                }`}
                style={{
                  alignItems: justifyContent,
                  justifyContent,
                  maxWidth,
                  paddingBottom,
                  paddingTop,
                  textAlign,
                  width,
                  minHeight,
                }}
              >
                <div
                  className={"footerTypeContainer"}
                  style={{
                    justifyContent,
                    textAlign,
                  }}
                >
                  {footerType}
                </div>
                <div
                  className={"footerContainer"}
                  style={{
                    justifyContent,
                    maxWidth,
                    textAlign,
                    width: footerTextWidth,
                  }}
                >
                  {label}
                </div>
              </div>
            </div>
          </div>
        );
      },
      [
        cellPadding,
        columnMetaDataMap,
        colWidths,
        dataHeaders,
        DIMENSION_COLUMNS_RIGHT_MARGIN,
        FOOTER_HEIGHT,
        FOOTER_PADDING,
        MAX_GRID_COL_WIDTH,
        MIN_ROW_HEIGHT,
        numDataCols,
        TABLE_RIGHT_PADDING,
        variant,
      ]
    );

    const superHeaderRenderer: CellRenderer<SuperHeader> = useMemo(
      () => ({ style = {}, classes = [], columnIndex, rowData }) => {
        const superData = rowData as SuperTopItemType<SuperHeader>;
        const ourClasses = ["superHeaderContainer"];
        const rightColumnIndex = columnIndex + superData.span - 1;
        if (dividerLines[`${columnIndex - 1}_${columnIndex}`]) {
          ourClasses.push("leftDivider");
        }
        if (dividerLines[`${rightColumnIndex}_${rightColumnIndex + 1}`]) {
          ourClasses.push("rightDivider");
        }
        if (dividerLines[rightColumnIndex]) {
          ourClasses.push("rightEndDivider");
        }
        if (superData && superData.data && superData.data.text.length) {
          ourClasses.push("hasText");
        }
        if (rightColumnIndex === numDataCols - 1) {
          ourClasses.push("end");
        }
        return (
          <div
            className={classes.join(" ")}
            style={{
              ...style,
              paddingTop: SUPER_HEADER_PADDING,
            }}
          >
            <div
              style={{
                padding: `0px ${cellPadding}px ${SUPER_HEADER_PADDING}px ${cellPadding}px`,
              }}
              className={ourClasses.join(" ")}
            >
              <div className="content innerCell">{superData.data.text}</div>
            </div>
          </div>
        );
      },
      [cellPadding, dividerLines, numDataCols]
    );

    const bottomData = hasAggregateData ? aggregateData : undefined;
    const leftData = hasDimensionData ? sortedDimensionData : undefined;
    const superTopData = hasSuperHeaders ? superHeaders : undefined;
    const topData = hasDataHeaders ? dataHeaders : undefined;

    return (
      <div className="metricsTable">
        <StickyTable<CellData, Column, Record<string, CellData>, CellData, undefined, SuperHeader>
          bottomData={bottomData}
          bottomRenderer={aggregateDataRenderer}
          bottomHeight={FOOTER_HEIGHT}
          cellRenderer={cellRenderer}
          columnWidth={columnWidths}
          cornerRenderer={dimensionHeaderRenderer}
          data={sortedTableData}
          height={height}
          leftData={leftData}
          leftRenderer={dimensionCellRenderer}
          leftWidth={
            leftColWidths && !R.isEmpty(leftColWidths)
              ? leftWidth + 2 * R.defaultTo(0, cellPadding) * R.keys(leftColWidths).length
              : dimensionColumns
              ? dimensionColumns.length * MAX_DIM_WIDTH
              : 0
          }
          overscan={(numRows ? numRows : 10) * 2} // We want to render every row
          rowHeight={i => rowHeight(i)}
          superTopData={superTopData}
          superTopHeight={SUPER_HEADER_HEIGHT}
          superTopRenderer={superHeaderRenderer}
          topData={topData}
          topHeight={headerContainerHeight}
          topRenderer={dataHeaderRenderer}
          width={width}
        />
        {shouldEnableMotionReplacement && (
          <CreativeTray
            key={adInfo.ad_name}
            isOpen={creativeTrayOpen}
            onClose={() => setCreativeTrayOpen(false)}
            metrics={creativeTrayMetrics}
            heatMaps={heatMaps}
            adInfo={adInfo}
            kpiName={kpiName}
          />
        )}
      </div>
    );
  }
);

export default MetricsTable;
